/**
 * Which brand logo the sidebar shows, from the hostname it is being served on.
 *
 * Preprod and production run the SAME admin bundle against different data — preprod has its own
 * detached database copy — so the only thing distinguishing the two on screen is the URL. The
 * badged logo makes it obvious at a glance which one you are looking at, which matters most in
 * the moment it is easiest to forget: acting on preprod data believing it is production.
 *
 * WHY HOSTNAME AND NOT A BUILD FLAG. A build-time env var would need the value threaded through
 * the deployment config, and those files are cicd-agent-owned (see /DEPLOYMENT-OWNERSHIP.md) —
 * CI blocks squad PRs that touch them. Reading the hostname needs no deploy change at all: the
 * same artifact renders differently depending on where it is served from. It also cannot drift
 * out of sync with reality, because the URL IS the environment.
 *
 * EXACT MATCH, NOT A PATTERN. `hostname.includes('preprod')` would be shorter and wrong: it
 * would badge any host with that substring, including one an attacker controls, and it would
 * silently stop badging if the preprod host is ever renamed. The same rule already governs
 * pages/settings/googleOAuthMessage.ts:34, tightened in 123b03c2a from a subdomain wildcard to
 * an exact allowlist. One definition of "is this preprod", stated the same way in both places.
 *
 * Production is the DEFAULT. An unrecognised host — localhost, a preview deploy, a future
 * domain — shows the normal logo. The failure mode that matters is a preprod screen wearing
 * production's logo, and that is exactly what this list prevents for the host we actually run.
 */

/** Hosts that serve preprod. Add to this list, never loosen the comparison. */
export const PREPROD_HOSTS = new Set(['admin-preprod.pagemind.ai']);

// The sidebar shows the wide wordmark when expanded and the square icon when collapsed. BOTH
// need the badge. Shipping only the wordmark left preprod indistinguishable from production for
// anyone with the sidebar collapsed — a persistent, user-controlled state — which both PR #5095
// reviewers correctly called blocking: the one state the feature exists to prevent was still
// reachable. Every environment-dependent brand asset belongs in this file for that reason.
export const PRODUCTION_LOGO = '/pagemind-logo.png?v=8';
export const PREPROD_LOGO = '/pagemind-logo-preprod.png?v=8';
export const PRODUCTION_ICON = '/pagemind-icon-transparent.png?v=7';
export const PREPROD_ICON = '/pagemind-icon-preprod.png?v=7';

export function isPreprodHost(hostname: string | undefined | null): boolean {
  if (!hostname) return false;
  return PREPROD_HOSTS.has(hostname.toLowerCase());
}

function currentHostname(): string | undefined {
  return typeof window !== 'undefined' ? window.location.hostname : undefined;
}

/**
 * Wide wordmark, for the expanded sidebar.
 * @param hostname defaults to the live location; injectable so this is testable without a DOM.
 */
export function brandLogoSrc(
  hostname: string | undefined | null = currentHostname()
): string {
  return isPreprodHost(hostname) ? PREPROD_LOGO : PRODUCTION_LOGO;
}

/**
 * Square icon, for the collapsed sidebar. Same predicate as brandLogoSrc — deliberately not a
 * second copy of the host check, so the two can never disagree about which environment this is.
 */
export function brandIconSrc(
  hostname: string | undefined | null = currentHostname()
): string {
  return isPreprodHost(hostname) ? PREPROD_ICON : PRODUCTION_ICON;
}
